import type { Account } from '@api/hooks/useAccounts';
import { ControlledAccountList } from '@components/AccountList';
import { ControlledTextInput } from '@components/ControlledTextInput';
import { FormBlocker } from '@components/FormBlocker';
import { InputDropdown } from '@components/input';
import { PageContainer } from '@components/PageContainer';
import { Switcher } from '@components/Switcher';
import { useGeoDataContext } from '@contexts/GeoDataContext';
import { Button, Chip, Typography } from '@gurupay/gp-ui-library';
import { useCreateTemplate } from '@hooks/useCreateTemplate';
import { useFF } from '@hooks/useFF';
import { useUpdateTemplate } from '@hooks/useUpdateTemplate';
import { resolveTemplateAddressModes } from '@pages/payments/utils/addressUtils';
import { EuroPayment, InternationalPayment } from '@pages/templates/components';
import { templateFormCleanUp } from '@utils/utils';
import { isAxiosError } from 'axios';
import type { FC } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CC_FEATURE_FLAGS } from 'types/layout';
import type { CreatePaymentTemplate } from 'types/templates';
import { TemplatePaymentType, TemplateType } from 'types/templates';

interface TemplatesFormProps {
	template?: CreatePaymentTemplate & { uuid: string };
	selectedAccount?: Account;
}

const paymentTypeChipLabel = {
	[TemplatePaymentType.INTERNAL]: 'templatesList.eurPayments',
	[TemplatePaymentType.DOMESTIC]: 'templatesList.eurPayments',
	[TemplatePaymentType.INTERNATIONAL]: 'templatesList.internationalPayments',
};

export const TemplatesForm: FC<TemplatesFormProps> = ({ template, selectedAccount }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const geoData = useGeoDataContext();
	const hasAddressAligned = useFF(CC_FEATURE_FLAGS.ADDRESS_ALIGNMENT);
	const { mutate: createTemplate, isPending: isPendingCreate } = useCreateTemplate();
	const { mutate: updateTemplate, isPending: isPendingUpdate } = useUpdateTemplate();
	const isPending = isPendingUpdate || isPendingCreate;
	const paymentTypes: { label: string; value: TemplatePaymentType }[] = [
		{
			label: t('templatesForm.internal'),
			value: TemplatePaymentType.INTERNAL,
		},
		{
			label: t('templatesForm.international'),
			value: TemplatePaymentType.INTERNATIONAL,
		},
	];
	const formSettings = useForm<CreatePaymentTemplate>({
		mode: 'onBlur',
		reValidateMode: 'onChange',
		defaultValues: {
			payment_type: TemplatePaymentType.INTERNAL,
			template_type: TemplateType.JOINT,
			currency: 'EUR',
			...resolveTemplateAddressModes(template, hasAddressAligned),
		},
	});

	const {
		control,
		handleSubmit,
		register,
		watch,
		setValue,
		formState,
		setError,
		clearErrors,
	} = formSettings;
	const onSubmit = (data: CreatePaymentTemplate) => {
		clearErrors();
		const saveOptions = {
			onSuccess: () => {
				navigate('/templates');
			},
			onError: (error: unknown) => {
				if (
					isAxiosError(error) &&
					error?.response?.data?.error_code === 'VALIDATION_FAILED'
				) {
					const errors = error?.response?.data?.errors ?? {};
					Object.keys(errors).forEach((errorKey) => {
						setError(errorKey as any, { message: errors[errorKey] });
					});
				}
			},
		};
		const payload = templateFormCleanUp(data, hasAddressAligned);

		if (template) {
			updateTemplate({ uuid: template.uuid, ...payload }, saveOptions);
		} else {
			createTemplate(payload, saveOptions);
		}
	};
	const isTouched = Object.keys(formState.dirtyFields).length > 0;

	const setDefaultValue = (accountId: string) => {
		setValue('account_uuid', accountId);
	};

	return (
		<PageContainer
			backUrl="/templates"
			title={
				<div className="flex w-full items-center gap-2">
					<span className="grow-0 shrink basis-auto min-w-0 truncate">
						{t(template ? 'templatesForm.editTitle' : 'templatesForm.title', {
							name: template?.name,
						})}
					</span>
					{!!template && (
						<Chip
							className="text-neutral-950 mr-space-2"
							color="primary"
							label={t(paymentTypeChipLabel[template.payment_type])}
							size="small"
							variant="outlined"
						/>
					)}
				</div>
			}
		>
			<FormProvider {...formSettings}>
				<form className="space-y-space-40" onSubmit={handleSubmit(onSubmit)}>
					<ControlledTextInput
						label={t('templatesForm.name')}
						name="name"
						rules={{ required: t('payments.newPayment.fieldIsRequired') }}
					/>
					{!template && (
						<Controller
							control={control}
							defaultValue={TemplatePaymentType.INTERNAL}
							name="payment_type"
							render={({ field }) => <Switcher {...field} options={paymentTypes} />}
						/>
					)}
					<ControlledAccountList
						defaultValue={selectedAccount}
						label={t('payments.common.from')}
						name="account_uuid"
						setDefaultValue={selectedAccount?.uuid ? undefined : setDefaultValue}
					/>
					<div className="space-y-space-16">
						<Typography level="h4">
							{t('payments.newPayment.additionalData.beneficiary')}
						</Typography>
						<ControlledTextInput
							label={t('payments.newPayment.form.name')}
							name="beneficiary.name"
							rules={{
								maxLength: {
									message: t('payments.common.maxLength', { maxLength: 70 }),
									value: 70,
								},
							}}
						/>
						<ControlledTextInput
							label={t('payments.newPayment.form.accountNo')}
							name="beneficiary.account"
						/>
					</div>
					<InputDropdown
						control={control}
						inputMode="decimal"
						inputRegister={register('amount')}
						isError={!!formState.errors?.amount}
						label={t('payments.newPayment.form.amount')}
						options={geoData?.data?.currencies ?? []}
						selectorRegister={register('currency', { required: true })}
					/>
					{watch('payment_type') === TemplatePaymentType.INTERNATIONAL ? (
						<InternationalPayment />
					) : (
						<EuroPayment />
					)}
					<div className="flex gap-space-16">
						<Button
							href="/templates"
							disabled={isPending || (!formState.isValid && formState.isDirty)}
							variant="outlined"
						>
							{t('templatesForm.cancel')}
						</Button>
						<Button
							disabled={isPending || (!formState.isValid && formState.isDirty)}
							type="submit"
						>
							{t(template ? 'templatesForm.saveChanges' : 'templatesForm.createTemplate')}
						</Button>
					</div>
					<FormBlocker
						descriptionKey="templateDescription"
						hasChanges={
							isTouched && !(formState.isSubmitting || formState.isSubmitSuccessful)
						}
					/>
				</form>
			</FormProvider>
		</PageContainer>
	);
};
